import { useState, useMemo, useEffect } from "react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown } from "lucide-react";
import ScreenCard from "@/components/ScreenCard";
import MediaCard from "@/components/MediaCard";
import DeviceCard from "@/components/DeviceCard";
import PromoSection from "@/components/dashboard/PromoSection";
import InsightsSection from "@/components/dashboard/InsightsSection";
import LeadsSection from "@/components/dashboard/LeadsSection";
import { SearchBar } from "@/components/ui/search-bar";
import { motion, AnimatePresence } from "framer-motion";
import { fetchMediasByQuery } from "@/hooks/useMedias";
import { useAuth } from "@/hooks/useAuth";
import { useUserPlan } from "@/hooks/useUserPlan";
import { fetchDevicesByQuery } from "@/hooks/useDevices";
import { useScreens } from "@/hooks/useScreens";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Search, Plus, Trash2, Edit, Play, Pause, Clock, Image as ImageIcon, Video as VideoIcon, Youtube as YoutubeIcon, FileVideo, FileImage, File, GripVertical, Lock } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { supabase } from '@/integrations/supabase/client';
import { BannerCarousel } from './BannerCarousel';

type Screen = {
    id: string;
    name: string;
    organization_id?: string;
    settings?: any;
    created_at?: string;
    updated_at?: string;
    [key: string]: any;
};

type Device = {
    id: string;
    name: string;
    code: string;
    status?: 'active' | 'inactive' | string;
    screen_id?: string;
    activated_at?: string;
    user_id?: string;
    [key: string]: any;
};

type Media = {
    id: string;
    title: string;
    description?: string;
    url: string;
    user_id?: string;
    organization_id?: string;
    selected_screens?: string[];
    display_time?: number;
    created_at?: string;
    updated_at?: string;
    last_updated_at?: string;
    active?: boolean;
    [key: string]: any;
};

type DashboardSectionsProps = {
    screens: Screen[];
    medias: Media[];
    devices: Device[];
    screensCountOverride?: number;
    handleEditScreen: (screen: any) => void;
    handleDeleteScreen: (screenId: string) => void;
    handleEditMedia: (media: any) => void;
    handleDeleteMedia: (mediaId: string) => void;
    handleUpdateDisplayTime: (mediaId: string, displayTime: number) => void;
    handleDeleteDevice: (deviceId: string) => void;
    handleUpdateDeviceScreen: (deviceId: string, screenId: string) => void;
    handleUpdateDeviceName: (deviceId: string, name: string) => Promise<void>;

    handleUpdateMediaOrder?: (mediaId: string, newOrderIndex: number) => void;
    handleUpdateMedia?: (mediaData: any) => Promise<any>;
};

const DashboardSections = ({
    screens = [],
    medias = [],
    devices = [],
    screensCountOverride,
    handleEditScreen,
    handleDeleteScreen,
    handleEditMedia,
    handleDeleteMedia,
    handleUpdateDisplayTime,
    handleDeleteDevice,
    handleUpdateDeviceScreen,

    handleUpdateMediaOrder,
    handleUpdateMedia,
}: DashboardSectionsProps) => {
    const [searchQuery, setSearchQuery] = useState("");
    const [filteredMedias, setFilteredMedias] = useState<Media[]>(medias);
    const [isDragging, setIsDragging] = useState(false); // Flag para controlar quando estamos fazendo drag
    const [isDevicesExpanded, setIsDevicesExpanded] = useState(false);
    const [expandedDeviceId, setExpandedDeviceId] = useState<string | null>(null);
    const [isMediasExpanded, setIsMediasExpanded] = useState(false);
    const [isScreensExpanded, setIsScreensExpanded] = useState(false);
    const { user } = useAuth();
    const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
    const impersonatedUserId = params.get('impersonate') || undefined;
    const isImpersonating = !!impersonatedUserId && !!user?.email && user.email.toLowerCase().includes('admin');
    const effectiveUserId = isImpersonating ? impersonatedUserId : (user?.id || '');
    const { hasActivePlan, loading: planLoading } = useUserPlan(effectiveUserId);
    const { fetchScreensByQuery } = useScreens();
    const [filteredDevices, setFilteredDevices] = useState<Device[]>(devices);
    const [filteredScreens, setFilteredScreens] = useState<Screen[]>(screens);
    const [screenSearchQuery, setScreenSearchQuery] = useState("");
    const { toast } = useToast();
    const [headerScreensCount, setHeaderScreensCount] = useState<number | undefined>(undefined);

    useEffect(() => {
        const computeHeaderCount = async () => {
            try {
                if (!isImpersonating) {
                    setHeaderScreensCount(undefined);
                    return;
                }
                // Preferir contagem passada via URL (sc) quando disponível
                const paramsUrl = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
                const scParam = paramsUrl.get('sc');
                if (scParam && !isNaN(Number(scParam))) {
                    setHeaderScreensCount(Number(scParam));
                    return;
                }
                if (effectiveUserId) {
                    const { data, error } = await supabase.rpc('admin_get_user_screens', { p_user_id: effectiveUserId });
                    if (!error && Array.isArray(data)) {
                        setHeaderScreensCount((data as any[]).length);
                        return;
                    }
                }
                // Fallback por organização do impersonation (via URL)
                const orgId = paramsUrl.get('org');
                if (orgId) {
                    const { data: byOrg, error: byOrgErr } = await supabase
                        .from('screens')
                        .select('id')
                        .eq('organization_id', orgId);
                    if (!byOrgErr && byOrg) {
                        setHeaderScreensCount((byOrg || []).length);
                        return;
                    }
                }
                // Fallback final: usar o prop screens
                setHeaderScreensCount(screens.length);
            } catch {
                setHeaderScreensCount(screens.length);
            }
        };
        computeHeaderCount();
    }, [isImpersonating, effectiveUserId, screens]);

    useEffect(() => {
        const rpcCount = typeof headerScreensCount === 'number' ? headerScreensCount : undefined;
        const propCount = screens.length;
        const overrideCount = typeof screensCountOverride === 'number' ? screensCountOverride : undefined;
        console.log('[CONSISTENCY] Grupos de Mídia contagem', {
            isImpersonating,
            effectiveUserId,
            rpcCount,
            overrideCount,
            hookPropCount: propCount,
            willShow: typeof headerScreensCount === 'number'
                ? headerScreensCount
                : (typeof screensCountOverride === 'number' ? screensCountOverride : propCount)
        });
    }, [headerScreensCount, screensCountOverride, screens, isImpersonating, effectiveUserId]);

    // Handler para deletar mídia mantendo a seção aberta
    const handleDeleteMediaWithSectionOpen = (mediaId: string) => {
        // Mantém a seção de mídias aberta
        setIsMediasExpanded(true);
        // Chama a função original de deletar
        handleDeleteMedia(mediaId);
    };

    // Handler para drag & drop das mídias
    const handleDragEnd = async (result: any) => {
        console.log('🎯 [DRAG_END] Resultado:', result);
        setIsDragging(false);

        if (!result.destination) {
            console.log('❌ [DRAG_END] Sem destination');
            return;
        }

        if (result.source.index === result.destination.index) {
            console.log('❌ [DRAG_END] Mesma posição');
            return;
        }

        const sourceIndex = result.source.index;
        const destIndex = result.destination.index;

        console.log('🔄 [DRAG_END] Movendo:', {
            de: sourceIndex,
            para: destIndex,
            item: filteredMedias[sourceIndex]?.title
        });

        // Criar nova array reordenada
        const newItems = [...filteredMedias];
        const [movedItem] = newItems.splice(sourceIndex, 1);
        newItems.splice(destIndex, 0, movedItem);

        // Atualizar order_index para refletir nova ordem
        const itemsWithNewOrder = newItems.map((item, index) => ({
            ...item,
            order_index: index
        }));

        console.log('📋 [DRAG_END] Nova ordem:', itemsWithNewOrder.map((item, idx) => `${idx}: ${item.title}`));

        // Atualizar estado local imediatamente
        setFilteredMedias(itemsWithNewOrder);

        // Atualizar estado local imediatamente
        setFilteredMedias(itemsWithNewOrder);

        try {
            // Atualizar order_index no banco de dados
            const updatePromises = itemsWithNewOrder.map((media, index) =>
                supabase
                    .from('medias')
                    .update({ order_index: index })
                    .eq('id', media.id)
                    .eq('user_id', user?.id)
            );

            const results = await Promise.all(updatePromises);

            // Verificar se todas as atualizações foram bem-sucedidas
            const errors = results.filter(result => result.error);
            if (errors.length > 0) {
                throw new Error(`Falhou ao atualizar ${errors.length} itens`);
            }

            console.log('✅ [DRAG_END] Ordem salva no banco com sucesso');

            toast({
                title: "Ordem atualizada!",
                description: "A nova ordem das mídias foi salva com sucesso",
            });

            // Disparar evento para sincronizar com o player
            window.dispatchEvent(new CustomEvent('dashboardDataUpdate', {
                detail: {
                    type: 'media_order_updated',
                    data: itemsWithNewOrder
                }
            }));

        } catch (error) {
            console.error('❌ [DRAG_END] Erro ao salvar ordem:', error);

            // Reverter mudança local em caso de erro
            setFilteredMedias(filteredMedias);

            toast({
                variant: "destructive",
                title: "Erro ao salvar ordem",
                description: "Não foi possível salvar a nova ordem das mídias",
            });
        }
    };

    // Handler para atualizar posição por número
    const handleUpdateMediaPosition = async (mediaId: string, newPosition: number) => {
        try {
            console.log('🔢 [POSITION_UPDATE] Alterando posição:', { mediaId, newPosition });

            // Validar nova posição
            if (newPosition < 1 || newPosition > filteredMedias.length) {
                console.warn('⚠️ [POSITION_UPDATE] Posição inválida:', newPosition);
                return;
            }

            // Encontrar a mídia atual
            const mediaIndex = filteredMedias.findIndex(m => m.id === mediaId);
            if (mediaIndex === -1) {
                console.warn('⚠️ [POSITION_UPDATE] Mídia não encontrada');
                return;
            }

            // Criar nova array com a reordenação
            const newItems = [...filteredMedias];
            const [movedMedia] = newItems.splice(mediaIndex, 1);
            newItems.splice(newPosition - 1, 0, movedMedia); // newPosition-1 porque é 0-indexed

            // Atualizar order_index para cada item
            const itemsWithNewOrder = newItems.map((item, index) => ({
                ...item,
                order_index: index
            }));

            console.log('📋 [POSITION_UPDATE] Nova ordem:', itemsWithNewOrder.map((item, idx) => `${idx + 1}: ${item.title}`));

            // Atualizar estado local
            setFilteredMedias(itemsWithNewOrder);

            // Salvar no banco de dados
            const updatePromises = itemsWithNewOrder.map((media, index) =>
                supabase
                    .from('medias')
                    .update({ order_index: index })
                    .eq('id', media.id)
                    .eq('user_id', user?.id)
            );

            const results = await Promise.all(updatePromises);

            // Verificar erros
            const errors = results.filter(result => result.error);
            if (errors.length > 0) {
                throw new Error(`Falhou ao atualizar ${errors.length} itens`);
            }

            console.log('✅ [POSITION_UPDATE] Posição salva com sucesso');

            toast({
                title: "Posição atualizada!",
                description: `Mídia movida para a posição ${newPosition}`,
            });

            // Sincronizar com o player
            window.dispatchEvent(new CustomEvent('dashboardDataUpdate', {
                detail: {
                    type: 'media_order_updated',
                    data: itemsWithNewOrder
                }
            }));

        } catch (error) {
            console.error('❌ [POSITION_UPDATE] Erro:', error);

            // Reverter estado em caso de erro
            // Vamos recarregar para garantir consistência
            setTimeout(() => {
                window.dispatchEvent(new CustomEvent('forceMediasRefresh'));
            }, 100);

            toast({
                variant: "destructive",
                title: "Erro ao alterar posição",
                description: "Não foi possível alterar a posição da mídia",
            });
        }
    };

    // Handler para quando o drag inicia
    const handleDragStart = (start: any) => {
        console.log('🎯 [DRAG_START] Iniciando drag:', start);
        setIsDragging(true);
    };

    // Log inicial para depuração
    useEffect(() => {
        console.log('📊 DashboardSections - Componente montado:', {
            totalMedias: medias.length,
            totalScreens: screens.length,
            totalDevices: devices.length,
            userId: user?.id
        });
    }, []);

    // Efeito para fechar dispositivos expandidos quando a seção for fechada
    useEffect(() => {
        if (!isDevicesExpanded) {
            setExpandedDeviceId(null);
        }
    }, [isDevicesExpanded]);

    // Efeito para manter a seção de mídias aberta após exclusões
    useEffect(() => {
        // Se a seção estava aberta antes de uma mudança na lista de mídias, mantém aberta
        const mediasChanged = medias.length !== filteredMedias.length;
        if (mediasChanged && isMediasExpanded) {
            // Força a seção a permanecer aberta após exclusão
            setTimeout(() => setIsMediasExpanded(true), 100);
        }
    }, [medias.length]);

    // Atualiza a lista de mídias ao digitar na busca ou quando medias prop muda
    useEffect(() => {
        // Não atualizar se estivermos no meio de um drag & drop
        if (isDragging) {
            console.log('🔄 DashboardSections - Ignorando atualização durante drag & drop');
            return;
        }

        const fetch = async () => {
            console.log('🔄 DashboardSections - Atualizando mídias filtradas:', {
                searchQuery: searchQuery.trim(),
                totalMedias: medias.length,
                userId: user?.id,
                hasUser: !!user?.id,
                isDragging
            });

            if (searchQuery.trim() === "") {
                // Usar as mídias diretamente sem reordenação adicional 
                // (a ordenação já vem do banco via order_index)
                const filtered = medias.map(media => ({
                    ...media,
                    order_index: (media as any).order_index ?? 0
                }));

                // Verificar mudanças de forma mais completa (não só IDs, mas também dados)
                const hasChanges = filtered.length !== filteredMedias.length ||
                    filtered.some((media, index) => {
                        const currentMedia = filteredMedias[index];
                        if (!currentMedia) return true;
                        // Comparar ID, título, descrição, URL e updated_at para detectar mudanças
                        return currentMedia.id !== media.id ||
                            currentMedia.title !== media.title ||
                            currentMedia.description !== media.description ||
                            currentMedia.url !== media.url ||
                            currentMedia.display_time !== media.display_time ||
                            JSON.stringify(currentMedia.selected_screens) !== JSON.stringify(media.selected_screens) ||
                            JSON.stringify(currentMedia.position_config) !== JSON.stringify(media.position_config) ||
                            currentMedia.updated_at !== media.updated_at;
                    });

                if (hasChanges) {
                    console.log('📋 DashboardSections - Aplicando mudanças nas mídias (dados atualizados)');
                    setFilteredMedias(filtered);
                }
            } else {
                try {
                    console.log('🔍 DashboardSections - Buscando mídias com query:', searchQuery);

                    const results = await fetchMediasByQuery(searchQuery, effectiveUserId || '');

                    if (results && Array.isArray(results)) {
                        const processedResults = results.map(media => ({
                            ...media,
                            order_index: (media as any).order_index || 0,
                            selected_screens: Array.isArray(media.selected_screens)
                                ? media.selected_screens
                                : []
                        }));

                        setFilteredMedias(processedResults);

                        console.log('✅ DashboardSections - Mídias filtradas encontradas:', processedResults.length);
                    } else {
                        console.log('⚠️ DashboardSections - Nenhuma mídia encontrada para a query');
                        setFilteredMedias([]);
                    }
                } catch (error) {
                    console.error('❌ DashboardSections - Erro ao buscar mídias:', error);
                    setFilteredMedias([]);
                }
            }
        };

        if (effectiveUserId) {
            fetch();
        }
    }, [searchQuery, medias, effectiveUserId, isDragging]);

    // Filtra os dispositivos baseado na busca
    useEffect(() => {
        const fetchDevices = async () => {
            if (searchQuery.trim() === "") {
                setFilteredDevices(devices.map(device => ({
                    ...device,
                    activated_at: (device as any).activated_at || "",
                    status: (device as any).status === 'active' ? 'active' : 'inactive',
                    user_id: (device as any).user_id || "",
                    organization_id: (device as any).organization_id || "",
                })));
            } else if (effectiveUserId) {
                const result = await fetchDevicesByQuery(effectiveUserId as string, searchQuery);
                setFilteredDevices(result.map(device => ({
                    ...device,
                    activated_at: (device as any).activated_at || "",
                    status: (device as any).status === 'active' ? 'active' : 'inactive',
                    user_id: (device as any).user_id || "",
                    organization_id: (device as any).organization_id || "",
                })));
            }
        };
        fetchDevices();
    }, [searchQuery, devices, user]);

    // Handler para expandir/retrair dispositivos
    const handleDeviceExpand = (deviceId: string) => {
        setExpandedDeviceId(currentId => currentId === deviceId ? null : deviceId);
    };

    // Calcula o número de mídias para cada tela de forma segura
    const getMediaCount = (screenId: string): number => {
        return medias.filter(media =>
            media.selected_screens &&
            Array.isArray(media.selected_screens) &&
            media.selected_screens.includes(screenId)
        ).length;
    };

    useEffect(() => {
        const fetchScreens = async () => {
            const normalizeSettings = (settings: any): { [key: string]: any; description?: string } | null => {
                if (!settings) return null;
                if (typeof settings === 'string') {
                    try {
                        return JSON.parse(settings);
                    } catch {
                        return null;
                    }
                }
                return settings;
            };
            if (screenSearchQuery.trim() === "") {
                setFilteredScreens(screens.map(screen => ({
                    ...screen,
                    created_at: screen.created_at || "",
                    updated_at: screen.updated_at || "",
                    settings: normalizeSettings(screen.settings)
                })));
            } else if (effectiveUserId) {
                const result = await fetchScreensByQuery(effectiveUserId as string, screenSearchQuery);
                setFilteredScreens(result.map(screen => ({
                    ...screen,
                    created_at: screen.created_at || "",
                    updated_at: screen.updated_at || "",
                    settings: normalizeSettings(screen.settings)
                })));
            }
        };
        fetchScreens();
    }, [screenSearchQuery, screens, effectiveUserId]);

    return (
        <div className="p-2 sm:p-4 md:p-6 lg:p-8 space-y-4 md:space-y-6 flex-1 max-w-full overflow-x-hidden">
            {/* Banner Carrossel Promocional - Gerenciado pelo Admin Master */}
            <BannerCarousel
                autoPlayInterval={6000}
                className="mb-2"
            />

            {/* Título da Seção - Estilo Facebook */}
            <div className="mb-4 md:mb-6">
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white">
                    Gerenciamento
                </h2>
                <p className="text-sm md:text-base text-gray-500 dark:text-gray-400 mt-0.5">
                    Grupos de Mídia · Mídias · Aparelhos
                </p>
            </div>

            {/* Seção de Grupos de Mídia */}
            <Collapsible
                className="border rounded-lg bg-card shadow-sm"
                open={isScreensExpanded}
                onOpenChange={setIsScreensExpanded}
            >
                <CollapsibleTrigger
                    className="flex items-center justify-between w-full p-3 sm:p-4 cursor-pointer"
                    onClick={(e) => {
                        if (!isImpersonating && !hasActivePlan && !planLoading) {
                            e.preventDefault();
                            e.stopPropagation();
                            window.dispatchEvent(new Event('openPlanModal'));
                            toast({ title: 'Assinatura necessária', description: 'Ative um plano para acessar Grupos de Mídia.' });
                        }
                    }}
                >
                    <h3 className="text-base sm:text-lg font-semibold text-foreground flex items-center gap-2">
                        Grupos de Mídia ({
                            // Priorizar screens.length se já tiver dados carregados
                            screens.length > 0
                                ? screens.length
                                : (typeof headerScreensCount === 'number'
                                    ? headerScreensCount
                                    : (typeof screensCountOverride === 'number' ? screensCountOverride : screens.length))
                        })
                        {!isImpersonating && !hasActivePlan && !planLoading && <Lock className="h-4 w-4 text-muted-foreground" aria-label="Recurso bloqueado" />}
                    </h3>
                    <ChevronDown className="h-4 w-4 sm:h-5 sm:w-5 transition-transform text-muted-foreground [&[data-state=open]]:rotate-180" />
                </CollapsibleTrigger>
                <CollapsibleContent className="p-2 sm:p-4 pt-0">
                    <div className="mb-4 w-full flex justify-end">
                        <div className="w-40 sm:w-64 mr-2 sm:mr-4 md:mr-8">
                            <SearchBar
                                value={screenSearchQuery}
                                onSearch={setScreenSearchQuery}
                                onClear={() => setScreenSearchQuery("")}
                                placeholder="Buscar grupos de mídia..."
                                onClick={e => e.stopPropagation && e.stopPropagation()}
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
                        {filteredScreens
                            .slice() // para não mutar o array original
                            .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
                            .map((screen) => (
                                <ScreenCard
                                    key={screen.id}
                                    screen={{
                                        id: screen.id,
                                        name: screen.name,
                                        settings: screen.settings || {},
                                        created_at: screen.created_at || '',
                                        ...screen
                                    }}
                                    mediasCount={getMediaCount(screen.id)}
                                    onEdit={handleEditScreen}
                                    onDelete={handleDeleteScreen}
                                />
                            ))}
                    </div>
                </CollapsibleContent>
            </Collapsible>

            {/* Seção de Mídias */}
            <Collapsible
                className="border rounded-lg bg-card shadow-sm"
                open={isMediasExpanded}
                onOpenChange={setIsMediasExpanded}
            >
                <CollapsibleTrigger
                    className="flex items-center justify-between w-full p-3 sm:p-4 cursor-pointer"
                    onClick={(e) => {
                        if (!isImpersonating && !hasActivePlan && !planLoading) {
                            e.preventDefault();
                            e.stopPropagation();
                            window.dispatchEvent(new Event('openPlanModal'));
                            toast({ title: 'Assinatura necessária', description: 'Ative um plano para acessar Mídias.' });
                        }
                    }}
                >
                    <h3 className="text-base sm:text-lg font-semibold text-foreground flex items-center gap-2">
                        Mídias ({filteredMedias.length})
                        {!isImpersonating && !hasActivePlan && !planLoading && <Lock className="h-4 w-4 text-muted-foreground" aria-label="Recurso bloqueado" />}
                    </h3>
                    <ChevronDown className="h-4 w-4 sm:h-5 sm:w-5 transition-transform text-muted-foreground [&[data-state=open]]:rotate-180" />
                </CollapsibleTrigger>
                <CollapsibleContent className="p-2 sm:p-4 pt-0">
                    <div className="mb-4 w-full flex justify-end">
                        <div className="w-40 sm:w-64 mr-2 sm:mr-4 md:mr-8">
                            <SearchBar
                                value={searchQuery}
                                onSearch={setSearchQuery}
                                onClear={() => setSearchQuery("")}
                                placeholder="Buscar mídias..."
                                onClick={e => e.stopPropagation && e.stopPropagation()}
                            />
                        </div>
                    </div>

                    {/* Drag & Drop Context */}
                    <DragDropContext
                        onDragEnd={handleDragEnd}
                        onDragStart={handleDragStart}
                        onDragUpdate={(update) => {
                            // Log para debug durante o drag
                            console.log('🔄 [DRAG_UPDATE]:', update);
                        }}
                    >
                        <Droppable
                            droppableId="medias-list"
                            type="MEDIA"
                        >
                            {(provided, snapshot) => (
                                <div
                                    {...provided.droppableProps}
                                    ref={provided.innerRef}
                                    className={`relative grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 md:gap-6 ${snapshot.isDraggingOver ? 'bg-muted/50 rounded-lg p-2 transition-colors' : ''
                                        }`}
                                >
                                    {/* Marcações de posição durante o drag */}
                                    {isDragging && (
                                        <>
                                            {/* Overlay com marcações de drop zones */}
                                            <div className="absolute inset-0 pointer-events-none z-0">
                                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 md:gap-6 h-full">
                                                    {Array.from({ length: filteredMedias.length }).map((_, index) => (
                                                        <div
                                                            key={`drop-zone-${index}`}
                                                            className="relative min-h-[200px] border-2 border-dashed border-green-300/60 bg-gradient-to-br from-green-50/40 to-green-100/30 rounded-xl transition-all duration-300 ease-in-out animate-fade-in"
                                                            style={{
                                                                animationDelay: `${index * 30}ms`,
                                                                animationDuration: '400ms',
                                                                animationFillMode: 'both'
                                                            }}
                                                        >
                                                            {/* Indicador central */}
                                                            <div className="absolute inset-0 flex items-center justify-center">
                                                                <div className="w-12 h-12 border-2 border-dashed border-green-400/70 rounded-full flex items-center justify-center bg-white/50 backdrop-blur-sm">
                                                                    <div className="w-4 h-4 bg-gradient-to-r from-green-400 to-green-500 rounded-full animate-pulse shadow-sm"></div>
                                                                </div>
                                                            </div>

                                                            {/* Número da posição */}
                                                            <div className="absolute top-2 right-2">
                                                                <span className="inline-flex items-center justify-center w-6 h-6 text-xs font-medium text-green-700 bg-white/80 rounded-full border border-green-200 shadow-sm">
                                                                    {index + 1}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>

                                            {/* Texto de instrução */}
                                            <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-8 z-30">
                                                <div className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg shadow-lg">
                                                    <div className="flex items-center space-x-2">
                                                        <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                                                        <span>Solte o card na posição desejada</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </>
                                    )}
                                    {filteredMedias.length === 0 ? (
                                        <div className="col-span-full text-center py-8 text-muted-foreground">
                                            {searchQuery.trim() ? (
                                                <p>Nenhuma mídia encontrada para "{searchQuery}"</p>
                                            ) : (
                                                <p>Nenhuma mídia configurada</p>
                                            )}
                                        </div>
                                    ) : (
                                        filteredMedias.map((media, index) => {
                                            const last_updated_at = (media as any).last_updated_at || media.updated_at || media.created_at;
                                            const orderIndex = (media as any).order_index ?? index;
                                            return (
                                                <Draggable
                                                    key={`${media.id}-${orderIndex}`}
                                                    draggableId={media.id}
                                                    index={index}
                                                    isDragDisabled={searchQuery.trim() !== ''}
                                                    type="MEDIA"
                                                >
                                                    {(provided, snapshot) => (
                                                        <div
                                                            ref={provided.innerRef}
                                                            {...provided.draggableProps}
                                                            className={`relative z-10 ${snapshot.isDragging
                                                                ? 'opacity-90 rotate-2 scale-105 shadow-2xl z-50 ring-2 ring-gray-400/50'
                                                                : 'hover:scale-102 transition-transform'
                                                                }`}
                                                        >
                                                            {/* Handle de arrastar visível e elegante - só mostrar quando não há busca */}
                                                            {searchQuery.trim() === '' && (
                                                                <div
                                                                    {...provided.dragHandleProps}
                                                                    className="absolute top-3 left-3 p-1.5 bg-slate-800/85 hover:bg-slate-700 rounded-lg cursor-move z-20 transition-all duration-200 shadow-lg backdrop-blur-sm border border-white/10 hover:border-white/20"
                                                                    title="Arraste para reordenar"
                                                                >
                                                                    <GripVertical className="h-3.5 w-3.5 text-white/90" />
                                                                </div>
                                                            )}

                                                            <div className="group">
                                                                <MediaCard
                                                                    media={{
                                                                        id: media.id,
                                                                        title: media.title,
                                                                        description: media.description || '',
                                                                        url: media.url,
                                                                        user_id: media.user_id,
                                                                        selected_screens: media.selected_screens || [],
                                                                        display_time: media.display_time || 5000,
                                                                        created_at: media.created_at,
                                                                        updated_at: media.updated_at || media.created_at,
                                                                        last_updated_at,
                                                                        active: media.active !== undefined ? media.active : true,
                                                                        order_index: (media as any).order_index || index,
                                                                        position_config: (media as any).position_config || null
                                                                    }}
                                                                    screens={screens}
                                                                    position={index + 1}
                                                                    totalCards={filteredMedias.length}
                                                                    onEdit={handleEditMedia}
                                                                    onDelete={() => handleDeleteMediaWithSectionOpen(media.id)}
                                                                    onUpdatePosition={handleUpdateMediaPosition}
                                                                    onUpdatePositionConfig={async (id, config) => {
                                                                        if (handleUpdateMedia) {
                                                                            await handleUpdateMedia({ id, position_config: config });
                                                                        }
                                                                    }}
                                                                />
                                                            </div>
                                                        </div>
                                                    )}
                                                </Draggable>
                                            );
                                        })
                                    )}
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </DragDropContext>
                </CollapsibleContent>
            </Collapsible>

            {/* Seção de Dispositivos */}
            <Collapsible
                className="border rounded-lg bg-card shadow-sm"
                open={isDevicesExpanded}
                onOpenChange={setIsDevicesExpanded}
            >
                <CollapsibleTrigger
                    className="flex items-center justify-between w-full p-3 sm:p-4 cursor-pointer"
                    onClick={(e) => {
                        if (isImpersonating) return;
                        if (!hasActivePlan && !planLoading) {
                            e.preventDefault();
                            e.stopPropagation();
                            window.dispatchEvent(new Event('openPlanModal'));
                            toast({ title: 'Assinatura necessária', description: 'Ative um plano para acessar Aparelhos.' });
                        }
                    }}
                >
                    <h3 className="text-base sm:text-lg font-semibold text-foreground flex items-center gap-2">
                        Aparelhos ({filteredDevices.length})
                        {!isImpersonating && !hasActivePlan && !planLoading && <Lock className="h-4 w-4 text-muted-foreground" aria-label="Recurso bloqueado" />}
                    </h3>
                    <ChevronDown className="h-4 w-4 sm:h-5 sm:w-5 transition-transform text-muted-foreground [&[data-state=open]]:rotate-180" />
                </CollapsibleTrigger>
                <CollapsibleContent className="p-2 sm:p-4 pt-0">
                    {isDevicesExpanded && (
                        <div className="mb-4 w-full flex justify-end">
                            <div className="w-40 sm:w-64 mr-2 sm:mr-4 md:mr-8">
                                <SearchBar
                                    value={searchQuery}
                                    onSearch={(value) => setSearchQuery(value)}
                                    onClear={() => setSearchQuery("")}
                                    placeholder="Buscar aparelhos..."
                                    onClick={(e) => e.stopPropagation()}
                                />
                            </div>
                        </div>
                    )}
                    <AnimatePresence>
                        <div className="grid grid-cols-1 gap-3 sm:gap-4">
                            {filteredDevices
                                .slice() // para não mutar o array original
                                .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
                                .map((device) => (
                                    <motion.div
                                        key={device.id}
                                        initial={{ opacity: 1 }}
                                        animate={{
                                            opacity: expandedDeviceId === null || expandedDeviceId === device.id ? 1 : 0,
                                            height: expandedDeviceId === null || expandedDeviceId === device.id ? "auto" : 0
                                        }}
                                        exit={{ opacity: 0, height: 0 }}
                                        transition={{ duration: 0.2 }}
                                        style={{ overflow: 'hidden' }}
                                    >
                                        <DeviceCard
                                            device={{
                                                id: device.id,
                                                code: device.code,
                                                name: device.name,
                                                status: (device.status === 'active' ? 'active' : 'inactive') as 'active' | 'inactive',
                                                activated_at: device.activated_at || '',
                                                user_id: device.user_id || '',
                                                ...device
                                            }}
                                            screens={screens}
                                            onDelete={handleDeleteDevice}
                                            onUpdateScreen={handleUpdateDeviceScreen}
                                            isExpanded={isDevicesExpanded && expandedDeviceId === device.id}
                                            onToggleExpand={() => handleDeviceExpand(device.id)}
                                        />
                                    </motion.div>
                                ))}
                        </div>
                    </AnimatePresence>
                </CollapsibleContent>
            </Collapsible>

            {/* Seção de Promoções */}
            <PromoSection />

            {/* Seção de Insights de Execução Local (Collapsed) */}
            <Collapsible
                className="border rounded-lg bg-card shadow-sm mt-6"
            >
                <CollapsibleTrigger
                    className="flex items-center justify-between w-full p-3 sm:p-4 cursor-pointer"
                >
                    <div className="flex flex-col items-start gap-1">
                        <h3 className="text-base sm:text-lg font-semibold text-foreground flex items-center gap-2">
                            Insights
                        </h3>
                        <p className="text-xs text-muted-foreground text-left">
                            Dados e métricas.
                        </p>
                    </div>
                    <ChevronDown className="h-4 w-4 sm:h-5 sm:w-5 transition-transform text-muted-foreground [&[data-state=open]]:rotate-180" />
                </CollapsibleTrigger>
                <CollapsibleContent className="p-4 pt-0">
                    <InsightsSection />
                </CollapsibleContent>
            </Collapsible>

            {/* Seção de Leads (Collapsed) */}
            <Collapsible
                className="border rounded-lg bg-card shadow-sm mt-6"
            >
                <CollapsibleTrigger
                    className="flex items-center justify-between w-full p-3 sm:p-4 cursor-pointer"
                >
                    <div className="flex flex-col items-start gap-1">
                        <h3 className="text-base sm:text-lg font-semibold text-foreground flex items-center gap-2">
                            Leads
                        </h3>
                        <p className="text-xs text-muted-foreground text-left">
                            Gerencie os leads capturados
                        </p>
                    </div>
                    <ChevronDown className="h-4 w-4 sm:h-5 sm:w-5 transition-transform text-muted-foreground [&[data-state=open]]:rotate-180" />
                </CollapsibleTrigger>
                <CollapsibleContent className="p-4 pt-0">
                    <LeadsSection />
                </CollapsibleContent>
            </Collapsible>
        </div>
    );
};

export default DashboardSections;
